/**
 * Which build this device is actually running, and whether it is the one the
 * server serves.
 *
 * This exists because "which build is this device on" was the unanswerable
 * question behind items 32 and 33. A service worker can pin an app to a build
 * that no longer exists on the server, and from the outside that app looks and
 * behaves perfectly -- it even works offline, because everything it needs is
 * in its own caches. Twice in one day a real device reported a bug that had
 * already been fixed, and the only way to tell was somebody on the far end
 * fetching the bundle and grepping it.
 *
 * **Nothing is injected at build time, deliberately.** The identity is read
 * from the entry bundle's own hashed filename, which Vite already derives from
 * the content. That costs no build configuration, cannot drift from the thing
 * it names, and preserves `stampServiceWorker`'s property that rebuilding
 * unchanged source produces an identical build -- a version string baked in
 * from the clock would have changed the bundle on every build and evicted
 * every client's shell for nothing.
 */

const ENTRY = /index-([A-Za-z0-9_-]+)\.js/
const SHELL_PREFIX = 'abg-shell-'

function hashIn(text: string | null | undefined): string | null {
  return text ? (ENTRY.exec(text)?.[1] ?? null) : null
}

/** This device's build, or `development` when the bundle is unhashed. */
export function currentBuild(): string {
  const entry = document.querySelector<HTMLScriptElement>('script[type="module"][src]')
  return hashIn(entry?.src) ?? hashIn(import.meta.url) ?? 'development'
}

/**
 * Whether this device is running what the server serves.
 *
 * `not-current` rather than "old": all this can honestly say is that the two
 * differ, and the server is the authority on which one counts.
 */
export type ServerState = 'current' | 'not-current' | 'unknown'

export interface BuildStatus {
  build: string
  /** The active service worker's cache version, if the Cache API answered. */
  cacheVersion: string | null
  server: ServerState
  serverBuild: string | null
}

export async function checkBuild(): Promise<BuildStatus> {
  const build = currentBuild()

  let cacheVersion: string | null = null
  try {
    const names = await caches.keys()
    cacheVersion = names.find((n) => n.startsWith(SHELL_PREFIX))?.slice(SHELL_PREFIX.length) ?? null
  } catch {
    // No Cache API, or a private window that refuses it. Not knowing the
    // cache version is a missing detail, not a failed check.
  }

  let serverBuild: string | null = null
  try {
    // The query string matters. A plain `/index.html` is intercepted by the
    // service worker and answered from the shell cache -- and that cache being
    // stale is precisely what this is checking for. A URL the worker has no
    // entry for reaches the network, and `no-store` keeps the browser's own
    // cache out of it too.
    const response = await fetch(`/index.html?build-check=${Date.now()}`, { cache: 'no-store' })
    if (response.ok) serverBuild = hashIn(await response.text())
  } catch {
    // Offline. Reported as `unknown`, never as `current` -- "we could not ask"
    // and "you are up to date" mean different things to somebody deciding
    // whether to trust what is on their screen.
  }

  const server: ServerState =
    serverBuild === null ? 'unknown' : serverBuild === build ? 'current' : 'not-current'

  return { build, cacheVersion, server, serverBuild }
}
